import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const NAV: Array<{ to: string; label: string; end: boolean; adminOnly?: boolean }> = [
  { to: '/dashboard', label: 'overview', end: true },
  { to: '/dashboard/certs', label: 'certifications', end: false },
  { to: '/dashboard/console', label: 'console', end: false },
  { to: '/dashboard/devices', label: 'devices', end: false },
  { to: '/dashboard/keys', label: 'api keys', end: false },
  { to: '/dashboard/members', label: 'members', end: false, adminOnly: true },
  { to: '/dashboard/api', label: 'REST API', end: false },
  { to: '/dashboard/mcp', label: 'MCP', end: false },
  { to: '/dashboard/settings', label: 'settings', end: false },
];

const navLinkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
  display: 'block',
  padding: '4px 0',
  color: isActive ? 'var(--color-accent)' : 'var(--color-text)',
  textDecoration: 'none',
  fontSize: 'var(--font-size-base)',
});

const externalLinkStyle: React.CSSProperties = {
  display: 'block',
  padding: '4px 0',
  color: 'var(--color-link)',
  textDecoration: 'none',
  fontSize: 'var(--font-size-base)',
};

/** Authenticated shell for `/dashboard/*` routes: sidebar nav, org info, logout, outlet. */
export function DashboardLayout() {
  const { user, isAdmin, logout } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const onUnauth = () => logout();
    window.addEventListener('astdz:unauthorized', onUnauth);
    return () => window.removeEventListener('astdz:unauthorized', onUnauth);
  }, [logout]);

  // Close mobile nav when route changes
  useEffect(() => {
    setMobileNavOpen(false);
  }, []);

  return (
    <div className="dashboard-shell">
      <button
        type="button"
        className="dashboard-mobile-toggle"
        onClick={() => setMobileNavOpen((v) => !v)}
        aria-label="toggle navigation"
        aria-expanded={mobileNavOpen}
      >
        {mobileNavOpen ? '[close]' : '[menu]'}
      </button>

      <aside className={`dashboard-sidebar ${mobileNavOpen ? 'open' : ''}`}>
        <div style={{ marginBottom: 'var(--spacing-sm)', letterSpacing: 1, fontWeight: 700 }}>
          AstdZ<span style={{ color: 'var(--color-accent)' }}>_</span>
        </div>
        <div className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>
          v1 <span style={{ color: 'var(--color-warn)', fontSize: '0.85em' }}>[BETA]</span>
        </div>
        <hr className="divider" />
        <nav onClick={() => setMobileNavOpen(false)}>
          {NAV.filter((item) => !item.adminOnly || isAdmin).map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} style={navLinkStyle}>
              {({ isActive }) => `${isActive ? '→' : ' '} ${item.label}`}
            </NavLink>
          ))}
        </nav>
        <hr className="divider" />
        <NavLink to="/docs" style={({ isActive }) => ({ ...externalLinkStyle, color: isActive ? 'var(--color-accent)' : 'var(--color-link)' })}>
          → documentation
        </NavLink>
        <NavLink to="/dashboard/sdk" style={({ isActive }) => ({ ...externalLinkStyle, color: isActive ? 'var(--color-accent)' : 'var(--color-link)' })}>
          → embedded sdk
        </NavLink>
        <NavLink
          to="/dashboard/support"
          style={({ isActive }) => ({ ...externalLinkStyle, color: isActive ? 'var(--color-accent)' : 'var(--color-link)' })}
          onClick={() => setMobileNavOpen(false)}
        >
          → support
        </NavLink>
        <a href="https://decode.astdz.com/emrg" target="_blank" rel="noreferrer" style={externalLinkStyle}>
          → decoder ↗
        </a>
        <a href="https://astdz.com" target="_blank" rel="noreferrer" style={externalLinkStyle}>
          ← astdz.com
        </a>
        <hr className="divider" />
        <div className="text-muted" style={{ fontSize: 'var(--font-size-sm)', wordBreak: 'break-word' }}>
          {user?.orgName ?? '—'}
        </div>
        <hr className="divider" />
        <button type="button" onClick={logout} className="dashboard-logout-btn">
          logout
        </button>
      </aside>
      <main className="dashboard-main">
        <Outlet />
      </main>
    </div>
  );
}
